// CB:BEGIN admin-users-page
import React, { useState, useEffect, useCallback } from 'react';
import ResponsiveContainer from '../../components/layout/ResponsiveContainer';
import SettingsCard from '../../components/ui/SettingsCard';
import { FaSearch, FaEdit, FaUserShield, FaUserTie, FaHome, FaTrash } from 'react-icons/fa';
import { useUser } from '@clerk/clerk-react';
import { useApi } from '@/lib/useApi';

interface AdminUser {
  id: string;
  email: string;
  role: 'HOMEOWNER' | 'CONTRACTOR' | 'ADMIN' | 'REALTOR';
  createdAt: string;
  homeowner?: { firstName?: string; lastName?: string; address?: string };
  contractor?: { company?: string; verified?: boolean };
}

interface UsersResponse {
  users: AdminUser[];
  pagination?: { page: number; limit: number; total: number; pages: number };
}

const ROLE_COLORS: Record<string, { bg: string; text: string; gradient: string }> = {
  ADMIN:      { bg: '#fef3c7', text: '#92400e', gradient: 'linear-gradient(135deg, #f59e0b, #d97706)' },
  CONTRACTOR: { bg: '#dbeafe', text: '#1e40af', gradient: 'linear-gradient(135deg, #3b82f6, #1d4ed8)' },
  HOMEOWNER:  { bg: '#dcfce7', text: '#166534', gradient: 'linear-gradient(135deg, #22c55e, #16a34a)' },
  REALTOR:    { bg: '#ede9fe', text: '#5b21b6', gradient: 'linear-gradient(135deg, #8b5cf6, #7c3aed)' },
};

const ROLE_ICON: Record<string, React.ReactNode> = {
  ADMIN:      <FaUserShield />,
  CONTRACTOR: <FaUserTie />,
  HOMEOWNER:  <FaHome />,
  REALTOR:    <FaUserTie />,
};

function displayName(u: AdminUser): string {
  if (u.homeowner?.firstName && u.homeowner?.lastName)
    return `${u.homeowner.firstName} ${u.homeowner.lastName}`;
  if (u.contractor?.company) return u.contractor.company;
  return u.email.split('@')[0];
}

function avatarInitial(u: AdminUser): string {
  if (u.homeowner?.firstName) return u.homeowner.firstName[0].toUpperCase();
  if (u.contractor?.company) return u.contractor.company[0].toUpperCase();
  return u.email[0].toUpperCase();
}

const AdminUsersPage: React.FC<{ onNavigate?: (screen: string, params?: any) => void }> = ({ onNavigate }) => {
  const { user, isLoaded } = useUser();
  const api = useApi();

  const [users, setUsers] = useState<AdminUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState<string>('all');
  const [changingRole, setChangingRole] = useState<string | null>(null);
  const [deletingUser, setDeletingUser] = useState<string | null>(null);

  const fetchUsers = useCallback(async () => {
    if (!user) return;
    try {
      setLoading(true);
      setError(null);
      const params = new URLSearchParams();
      if (roleFilter !== 'all') params.append('role', roleFilter);
      const data = await api.get<UsersResponse>(`/api/admin/users?${params}`);
      setUsers(data?.users ?? []);
    } catch (e: any) {
      setError(e?.message ?? 'Failed to load users');
    } finally {
      setLoading(false);
    }
  }, [user, roleFilter]);

  useEffect(() => {
    if (isLoaded && user) fetchUsers();
  }, [isLoaded, user, fetchUsers]);

  const handleDelete = async (u: AdminUser) => {
    const profileNote = u.homeowner
      ? ' (and their homeowner profile + projects)'
      : u.contractor
      ? ' (and their contractor profile + bids)'
      : '';
    if (!window.confirm(`Permanently delete "${u.email}"${profileNote}? This cannot be undone.`)) return;
    setDeletingUser(u.id);
    try {
      await api.delete(`/api/admin/users/${u.id}`);
      setUsers(prev => prev.filter(x => x.id !== u.id));
    } catch (e: any) {
      alert(`Error: ${e?.message ?? 'Failed to delete user'}`);
    } finally {
      setDeletingUser(null);
    }
  };

  const handleChangeRole = async (userId: string, newRole: string) => {
    if (!window.confirm(`Change this user's role to ${newRole}?`)) return;
    setChangingRole(userId);
    try {
      await api.patch(`/api/admin/users/${userId}/role`, { role: newRole });
      await fetchUsers();
    } catch (e: any) {
      alert(`Error: ${e?.message ?? 'Failed to change role'}`);
    } finally {
      setChangingRole(null);
    }
  };

  const filtered = users.filter(u =>
    u.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
    displayName(u).toLowerCase().includes(searchTerm.toLowerCase())
  );

  const showSpinner = !isLoaded || loading;

  return (
    <ResponsiveContainer className="container">
      <div className="content">
        {showSpinner && (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          </div>
        )}

        {!showSpinner && (
          <>
            {error && (
              <div style={{ background: '#FEF2F2', border: '1px solid #FECACA', borderRadius: 8, padding: 12, marginBottom: 16, color: '#991B1B' }}>
                {error}
                <button onClick={fetchUsers} style={{ marginLeft: 12, textDecoration: 'underline', background: 'none', border: 'none', color: '#991B1B', cursor: 'pointer' }}>Retry</button>
              </div>
            )}

            {/* Header */}
            <div style={{ marginBottom: 32 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  <h1 style={{ fontSize: 28 }}>All Users</h1>
                  <p style={{ color: '#6b7280', marginTop: 8 }}>
                    Manage user accounts and roles — {users.length} user{users.length !== 1 ? 's' : ''} total
                  </p>
                </div>
              </div>

              {/* Role filter buttons */}
              <div style={{ marginTop: 16, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                {(['all', 'HOMEOWNER', 'CONTRACTOR', 'ADMIN', 'REALTOR'] as const).map(r => (
                  <button
                    key={r}
                    onClick={() => setRoleFilter(r)}
                    className={`btn ${roleFilter === r ? 'btn-primary' : 'btn-secondary'}`}
                    style={{ fontSize: 12, padding: '6px 12px' }}
                  >
                    {r === 'all' ? 'All Roles' : r.charAt(0) + r.slice(1).toLowerCase()}
                  </button>
                ))}
              </div>
            </div>

            {/* Search */}
            <SettingsCard title="Search Users">
              <div style={{ position: 'relative' }}>
                <div style={{ position: 'absolute', top: '50%', left: 12, transform: 'translateY(-50%)', pointerEvents: 'none' }}>
                  <FaSearch style={{ width: 20, height: 20, color: '#9ca3af' }} />
                </div>
                <input
                  type="text"
                  placeholder="Search by name or email…"
                  value={searchTerm}
                  onChange={e => setSearchTerm(e.target.value)}
                  className="form-control"
                  style={{ paddingLeft: 40 }}
                />
              </div>
            </SettingsCard>

            {/* User list */}
            <SettingsCard title={`Users (${filtered.length})`}>
              {filtered.length > 0 ? (
                <div className="space-y-4">
                  {filtered.map(u => {
                    const roleStyle = ROLE_COLORS[u.role] ?? ROLE_COLORS.HOMEOWNER;
                    const name = displayName(u);
                    const initial = avatarInitial(u);
                    const subtext = u.homeowner?.address || (u.contractor?.verified != null ? (u.contractor.verified ? 'Verified contractor' : 'Unverified contractor') : null);

                    return (
                      <div
                        key={u.id}
                        className="project-card"
                        style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}
                      >
                        {/* Left: avatar + info */}
                        <div style={{ display: 'flex', alignItems: 'center', gap: 16, flex: '1 1 240px', minWidth: 0 }}>
                          <div style={{
                            width: 48, height: 48, borderRadius: '50%',
                            background: roleStyle.gradient,
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            flexShrink: 0,
                          }}>
                            <span style={{ fontSize: 16, fontWeight: 600, color: 'white' }}>{initial}</span>
                          </div>
                          <div style={{ minWidth: 0 }}>
                            <div style={{ fontSize: 17, fontWeight: 600, color: '#111827', marginBottom: 2 }}>{name}</div>
                            <div style={{ fontSize: 14, color: '#6b7280', marginBottom: subtext ? 2 : 0 }}>{u.email}</div>
                            {subtext && <div style={{ fontSize: 13, color: '#9ca3af' }}>{subtext}</div>}
                            <div style={{ fontSize: 12, color: '#9ca3af', marginTop: 2 }}>
                              Joined {new Date(u.createdAt).toLocaleDateString()}
                            </div>
                          </div>
                        </div>

                        {/* Right: role badge + actions */}
                        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                          {/* Role badge */}
                          <span style={{
                            display: 'inline-flex', alignItems: 'center', gap: 5,
                            padding: '4px 10px', borderRadius: 20, fontSize: 12, fontWeight: 600,
                            background: roleStyle.bg, color: roleStyle.text,
                          }}>
                            {ROLE_ICON[u.role]}
                            {u.role.charAt(0) + u.role.slice(1).toLowerCase()}
                          </span>

                          {/* Change role */}
                          <select
                            value={u.role}
                            disabled={changingRole === u.id}
                            onChange={e => handleChangeRole(u.id, e.target.value)}
                            className="btn btn-secondary"
                            style={{ fontSize: 12, padding: '5px 10px', height: 'auto', cursor: 'pointer' }}
                            title="Change role"
                          >
                            <option value="HOMEOWNER">Homeowner</option>
                            <option value="CONTRACTOR">Contractor</option>
                            <option value="ADMIN">Admin</option>
                            <option value="REALTOR">Realtor</option>
                          </select>

                          {/* Edit */}
                          <button
                            onClick={() => onNavigate?.('admin-users-edit', { userId: u.id })}
                            className="btn btn-secondary"
                            style={{ fontSize: 12, padding: '6px 12px' }}
                          >
                            <FaEdit style={{ marginRight: 4 }} />
                            Edit
                          </button>

                          {/* Delete */}
                          <button
                            onClick={() => handleDelete(u)}
                            disabled={deletingUser === u.id}
                            className="btn"
                            style={{ fontSize: 12, padding: '6px 12px', backgroundColor: '#dc2626', color: 'white', border: 'none', opacity: deletingUser === u.id ? 0.6 : 1 }}
                          >
                            <FaTrash style={{ marginRight: 4 }} />
                            {deletingUser === u.id ? '…' : 'Delete'}
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div style={{ textAlign: 'center', padding: '40px 20px', color: '#6b7280' }}>
                  <div style={{ fontSize: 48, marginBottom: 16, opacity: 0.3 }}>👤</div>
                  <p style={{ margin: 0, fontWeight: 500 }}>No users found</p>
                  <p style={{ margin: '8px 0 0', fontSize: 14, opacity: 0.8 }}>
                    {searchTerm ? 'Try adjusting your search' : roleFilter !== 'all' ? `No ${roleFilter.toLowerCase()} users yet` : 'No users registered yet'}
                  </p>
                </div>
              )}
            </SettingsCard>
          </>
        )}
      </div>
    </ResponsiveContainer>
  );
};

export default AdminUsersPage;
// CB:END admin-users-page
